import Vue from 'vue'
import VueRouter from 'vue-router'
import Home from './views/Home.vue'
import ArticlesIndex from './views/ArticlesIndex.vue'
import articles from './articles/registry'

Vue.use(VueRouter)

const DEFAULT_TITLE = 'EyeJack Creator — WebAR Platform for Artists'

// One route per registered article (explicit, so the prerender step can
// enumerate every article URL). strict:false → a trailing slash is optional.
const articleRoutes = articles.map((a) => ({
  path: '/articles/' + a.slug,
  name: 'article-' + a.slug,
  component: a.component,
  meta: { title: a.title, description: a.description }
}))

// History mode: public URLs are clean (/ , /articles, /articles/<slug>). Asset
// URLs stay under /landing/ (webpack publicPath) — independent of the router base,
// which is '/'. The prerender step snapshots each route to static HTML so crawlers
// and social scrapers get real content; the SPA hydrates on top.
const router = new VueRouter({
  mode: 'history',
  routes: [
    { path: '/', name: 'home', component: Home, meta: { title: DEFAULT_TITLE } },
    { path: '/articles', name: 'articles', component: ArticlesIndex, meta: { title: 'Articles — EyeJack Creator' } },
    ...articleRoutes
  ],
  scrollBehavior (to, from, savedPosition) {
    // Hash targets (e.g. /#features from an article): wait a beat so the home
    // view has mounted before scrolling. Section offset comes from CSS
    // scroll-margin-top (set on #features etc. in landing.scss).
    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(() => resolve({ selector: to.hash }), 300)
      })
    }
    if (savedPosition) return savedPosition
    return { x: 0, y: 0 }
  }
})

router.afterEach((to) => {
  Vue.nextTick(() => {
    document.title = (to.meta && to.meta.title) || DEFAULT_TITLE
  })
})

// Prerender manifest — read by scripts/prerender.mjs to enumerate every route
// and its SEO <head>. Canonical/OG use the prod host; the prerender rewrites the
// OG *serving* host per stage. Home ('/') keeps the template head as-is.
const PROD = 'https://creator.eyejack.io'
const abs = (p) => (p && p.startsWith('http') ? p : PROD + p)
if (typeof window !== 'undefined') {
  window.__PRERENDER_MANIFEST__ = [
    { path: '/', waitFor: '.ej-home', rewriteHead: false },
    {
      path: '/articles',
      waitFor: '.a-index-hero',
      rewriteHead: true,
      title: 'Articles — EyeJack Creator',
      description: 'Guides and stories on creating augmented reality art with EyeJack Creator — WebAR how-tos, artist features, and practical tips.',
      url: PROD + '/articles/',
      ogType: 'website',
      ogImage: abs('/landing/social-card.jpg')
    },
    ...articles.map((a) => ({
      path: '/articles/' + a.slug,
      waitFor: '.article-page',
      rewriteHead: true,
      noindex: !!a.noindex,
      title: a.title,
      description: a.description,
      url: PROD + '/articles/' + a.slug + '/',
      ogType: 'article',
      ogImage: abs(a.ogImage || '/landing/social-card.jpg'),
      date: a.date,
      author: a.author,
      // Short, human name for the BreadcrumbList leaf (the SEO title is too long).
      breadcrumb: a.cardTitle || a.title
    }))
  ]
}

export default router
